import { Box, Button, FormControl, FormGroup, FormHelperText, FormLabel, Link, Stack, Typography } from "@mui/material";
import { MdsAddBox, MdsChips, MdsContentCopy } from "@sinch/icons";
import { getWeekdayName } from "@sinch/utils/dateTime/format";
import { always, ifElse, isEmpty, isNil, last } from "ramda";
import React from "react";
import { FieldPath, FieldValues, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { InferType } from "yup";
import { MdsIcon, useMuiController } from "@sinch/components";
import { intervalTypeTimeSchema, timeRange } from "@sinch/components/application/TimeIntervalSettings/formSchema";
import { StringTimeInput } from "@sinch/components/application/TimeIntervalSettings/StringTimeInput";
import { CheckboxInput } from "@sinch/components/form/CheckboxInput/CheckboxInput";
import { Pill } from "@sinch/components/Pill/Pill";

export type TimeIntervalTypeFormFields = InferType<typeof intervalTypeTimeSchema>;
const pathPrefix =
  <TFieldValues extends FieldValues = FieldValues>(prefixString: FieldPath<TFieldValues> | null) =>
  <TString extends string = string>(strings: TemplateStringsArray, ...values: any[]) => {
    // Combine the template literal parts
    let result = "";
    for (let i = 0; i < strings.length; i++) {
      result += strings[i];
      if (i < values.length) {
        result += values[i];
      }
    }
    // Add prefix to the beginning
    return (ifElse(isNil, always(""), (str) => `${str}.`)(prefixString) + result) as TString;
  };

interface TimeIntervalSettingsProps<
  TFieldValues extends FieldValues = FieldValues,
  TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  // Path is prefix of form fields name (without dot at end of string)
  path?: TFieldName | null;
}
export const TimeIntervalSettings = <TFieldValues extends FieldValues = FieldValues>({
  path,
}: TimeIntervalSettingsProps<TFieldValues>) => {
  // prefix for form field name
  const { t } = useTranslation();
  const { getValues, setValue } = useFormContext<TimeIntervalTypeFormFields>();
  const prefix = pathPrefix<TFieldValues>(path ?? null);

  const isFirstEnabled = useWatch<TimeIntervalTypeFormFields>({
    name: prefix<`daysOfWeek.${number}.enabled`>`daysOfWeek.${0}.enabled`,
  });

  const handleCopyFirst = () => {
    const firstDayValues = getValues(prefix<`daysOfWeek.${number}`>`daysOfWeek.${0}`);
    Array.from({ length: 7 }, (_, i) => i).map((i) => {
      setValue(prefix<`daysOfWeek.${number}.enabled`>`daysOfWeek.${i}.enabled`, firstDayValues.enabled);
      setValue(prefix<`daysOfWeek.${number}.times`>`daysOfWeek.${i}.times`, firstDayValues.times);
    });
  };

  return (
    <FormGroup>
      <Stack direction="row" justifyContent="space-between">
        <FormLabel>{t("RateUnit.weekdays")}</FormLabel>
        <Link
          onClick={() => isFirstEnabled && handleCopyFirst()}
          sx={{
            minWidth: "auto",
            maxHeight: "32px",
            px: 0,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            fontWeight: 600,
            color: (theme) => (isFirstEnabled ? null : theme.palette.action.disabled),
            pointerEvents: !isFirstEnabled ? "none" : null,
          }}
        >
          <MdsIcon fontSize="xs" icon={MdsContentCopy} />
          <span>{t("RateUnit.applyFirstToAll")}</span>
        </Link>
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr max-content",
          gap: 1,
          rowGap: 0.5,
          justifyItems: "start",
          alignItems: "center",
        }}
      >
        {Array.from({ length: 7 }, (_, i) => (
          <DayOfWeekRow key={i} prefix={prefix} weekday={i} />
        ))}
      </Box>
    </FormGroup>
  );
};

const DayOfWeekRow = ({
  weekday,
  prefix,
}: {
  weekday: number;
  prefix: <TString extends string = string>(strings: TemplateStringsArray, ...values: any[]) => TString;
}) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext<TimeIntervalTypeFormFields>();

  const { field: daysField } = useMuiController<TimeIntervalTypeFormFields, `daysOfWeek.${number}.enabled`>({
    name: prefix<`daysOfWeek.${number}.enabled`>`daysOfWeek.${weekday}.enabled`,
  });
  const {
    field: { error, errorMessage },
  } = useMuiController<TimeIntervalTypeFormFields, `daysOfWeek.${number}.times`>({
    name: prefix`daysOfWeek.${weekday}.times`,
  });
  const { fields, append, remove } = useFieldArray<TimeIntervalTypeFormFields>({
    name: prefix`daysOfWeek.${weekday}.times`, // unique name for your Field Array
  });

  const handleChangeEnabled = (checked: boolean) => {
    if (!checked) {
      setValue(prefix<`daysOfWeek.${number}.times`>`daysOfWeek.${weekday}.times`, []);
    }
    daysField.onChange(checked);
  };

  return (
    <>
      <Box sx={{ gridColumn: "1/3" }}></Box>
      <Box sx={{ gridColumn: "1/1", maxHeight: "32px", display: "flex", alignItems: "center" }}>
        <CheckboxInput
          {...daysField}
          color="info"
          FormControlProps={{ fullWidth: false }}
          label={getWeekdayName(weekday + 1)}
          onChange={(__, checked) => handleChangeEnabled(checked)}
          value={daysField.value}
        />
      </Box>
      {daysField.value && (
        <>
          {isEmpty(fields) && daysField.value && (
            <>
              <Box sx={{ gridColumn: "2/2", width: "100%" }}>
                <Pill
                  label={<Typography>{t("RateUnit.entireDay")}</Typography>}
                  sx={{ minHeight: "32px", width: "100%" }}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ gridColumn: "3/3" }}>
                <Button
                  color="success"
                  onClick={() => append(timeRange.getDefault())}
                  size="medium"
                  sx={{ minWidth: "auto", maxHeight: "32px", px: 0 }}
                  variant="text"
                >
                  <MdsIcon fontSize="small" icon={MdsAddBox} />
                </Button>
              </Box>
            </>
          )}
          {fields.map((field, index) => (
            <>
              <Box sx={{ gridColumn: "2/2" }}>
                <TimeRangeItem key={field.id} index={index} prefix={prefix} weekday={weekday} />
              </Box>
              <Box sx={{ gridColumn: "3/3" }}>
                <Stack key={field.id} alignItems="center" direction="row" gap={0.5}>
                  <Button
                    color="error"
                    onClick={() => remove(index)}
                    size="medium"
                    sx={{
                      minWidth: "auto",
                      maxHeight: "32px",
                      px: 0,
                      color: "grey.500",
                      "&:hover": { color: "error.main" },
                    }}
                    variant="text"
                  >
                    <MdsIcon fontSize="small" icon={MdsChips} />
                  </Button>
                  {field === last(fields) && (
                    <Button
                      color="success"
                      onClick={() => append(timeRange.getDefault())}
                      size="medium"
                      sx={{ minWidth: "auto", maxHeight: "32px", px: 0 }}
                      variant="text"
                    >
                      <MdsIcon fontSize="small" icon={MdsAddBox} />
                    </Button>
                  )}
                </Stack>
              </Box>
            </>
          ))}
          {error && (
            <Box sx={{ gridColumn: "2/2" }}>
              {errorMessage.map((label) => (
                <FormHelperText key={label} error>
                  {label}
                </FormHelperText>
              ))}
            </Box>
          )}
        </>
      )}
    </>
  );
};

const TimeRangeItem = ({
  weekday,
  index,
  prefix,
}: {
  weekday: number;
  index: number;
  prefix: <TString extends string = string>(strings: TemplateStringsArray, ...values: any[]) => TString;
}) => {
  const {
    field: { errorMessage: fromErrMsg, error: fromErr, ...fromField },
  } = useMuiController<TimeIntervalTypeFormFields, `daysOfWeek.${number}.times.${number}.from`>({
    name: prefix<`daysOfWeek.${number}.times.${number}.from`>`daysOfWeek.${weekday}.times.${index}.from`,
  });
  const {
    field: { errorMessage: toErrMsg, error: toErr, ...toField },
  } = useMuiController<TimeIntervalTypeFormFields, `daysOfWeek.${number}.times.${number}.to`>({
    name: prefix<`daysOfWeek.${number}.times.${number}.to`>`daysOfWeek.${weekday}.times.${index}.to`,
  });
  return (
    <FormControl error={fromErr || toErr}>
      <Stack alignItems="center" direction="row" spacing={0.5} sx={{ maxWidth: "200px" }}>
        <StringTimeInput
          {...fromField}
          error={fromErr}
          FormControlProps={{ sx: { maxWidth: "calc(50% - 8px)" } }}
          fullWidth={false}
          size="small"
          sx={{ "&>input.MuiInputBase-input": { textAlign: "center", p: 0 } }}
        />
        <Box sx={{ width: "8px", textAlign: "center" }}>-</Box>
        <StringTimeInput
          {...toField}
          error={toErr}
          FormControlProps={{ sx: { maxWidth: "calc(50% - 8px)" } }}
          fullWidth={false}
          includeTime="end"
          size="small"
          sx={{ "&>input.MuiInputBase-input": { textAlign: "center", p: 0 } }}
        />
      </Stack>
      {[...fromErrMsg, ...toErrMsg].map((label) => (
        <FormHelperText key="label">{label}</FormHelperText>
      ))}
    </FormControl>
  );
};
